/**
 * FikoSketchAraci.ts — Sketch için interaktif çizim aracı (motor çekirdeği)
 *
 * Kendi pointer dinleyicileri + raycaster ile çalışır; mevcut seçim/hover
 * sistemine dokunmaz. Düzleme ray-plane kesişimi yapar, grid + uç-nokta snap
 * uygular, canlı önizleme gösterir. baslat()/durdur() ile aç-kapa.
 */

import * as THREE from 'three';
import { FikoSketch, ISketchNokta, SketchTip } from './FikoSketch';
import { FikoSnapMotoru, SnapSonuc } from './FikoSnapMotoru';

export type SketchArac = 'sec' | 'cizgi' | 'dikdortgen' | 'dikdortgenMerkez' | 'cember' | 'cember3n' | 'yay' | 'polyline' | 'cokgen' | 'slot' | 'nokta' | 'insacizgi' | 'elips' | 'spline'
  | 'konik' | 'sablonDik' | 'sablonKeyhole' | 'sablonParalel';   // CATIA: mouse ile yerleştirilen şablon/konik (2-tık)

const RENK_ONIZLEME = 0x9affc0;
const RENK_INFERENCE = 0xffa726;

export class FikoSketchAraci {
  arac: SketchArac = 'sec';
  gridAdim = 1;          // mm — 0 ise grid snap kapalı
  snapAcik = true;
  cokgenKenar = 6;       // çokgen kenar sayısı (3-20)
  readonly snap = new FikoSnapMotoru();

  private aktif = false;
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private duzlemTHREE = new THREE.Plane();
  private noktalar: ISketchNokta[] = [];
  private tegetYayModu = false;            // polyline'da G tuşu → çizgi↔teğet yay (CATIA Profile)
  private sonTeget: ISketchNokta | null = null;   // önceki segment bitiş teğeti (yay için)
  private sonMouse: ISketchNokta | null = null;   // sayısal giriş için son mouse düzlem konumu
  sonMouseEkran: { x: number; y: number } | null = null;   // sayısal panel imlece yakın dursun
  private shiftBasili = false;
  private onizleme: THREE.Line | null = null;
  private onizlemeMat = new THREE.LineBasicMaterial({ color: RENK_ONIZLEME });

  // snap görselleri
  private markerEl: HTMLDivElement | null = null;
  private inferenceCizgi: THREE.LineSegments | null = null;
  private inferenceMat = new THREE.LineDashedMaterial({ color: RENK_INFERENCE, dashSize: 1.5, gapSize: 1 });

  private hDown: ((e: PointerEvent) => void) | null = null;
  private hMove: ((e: PointerEvent) => void) | null = null;
  private hUp: ((e: PointerEvent) => void) | null = null;
  private hKey: ((e: KeyboardEvent) => void) | null = null;
  private hKeyUp: ((e: KeyboardEvent) => void) | null = null;
  // sürükleme (interaktif düzenleme)
  private surukle: { el: any; key: string; son: ISketchNokta; tasindi: boolean; grup?: { el: any; idx: number }[] } | null = null;

  constructor(
    private sketch: FikoSketch,
    private canvasAl: () => HTMLCanvasElement | null | undefined,
    private kameraAl: () => THREE.Camera | null | undefined,
    private sahne: THREE.Object3D,
    private ciz: () => void,                 // render iste
    private degisti?: () => void,            // eleman eklenince UI'a haber
    private surukleBitti?: () => void,       // sürükleme bittiğinde (feature ise rebuild)
    private kontrolKilit?: (kilit: boolean) => void, // sürükleme sırasında kamerayı kilitle
  ) {}

  get acikMi(): boolean { return this.aktif; }

  baslat(): void {
    if (this.aktif) return;
    const canvas = this.canvasAl();
    if (!canvas) return;
    this.duzlemGuncelle();
    this.hDown = (e) => this.onDown(e, canvas);
    this.hMove = (e) => this.onMove(e, canvas);
    this.hUp = (e) => this.onUp(e);
    this.hKey = (e) => {
      if (e.key === 'Escape') this.iptal();
      else if (e.key === 'Shift') this.shiftBasili = true;
      else if ((e.key === 'g' || e.key === 'G') && this.arac === 'polyline' && this.noktalar.length) { this.tegetYayModu = !this.tegetYayModu; if (this.sonMouse) this.onizlemeGuncelle([...this.noktalar, this.sonMouse]); }
    };
    this.hKeyUp = (e) => { if (e.key === 'Shift') this.shiftBasili = false; };
    canvas.addEventListener('pointerdown', this.hDown);
    canvas.addEventListener('pointermove', this.hMove, { passive: true });
    window.addEventListener('pointerup', this.hUp);
    window.addEventListener('keydown', this.hKey);
    window.addEventListener('keyup', this.hKeyUp);
    this.aktif = true;
  }

  durdur(): void {
    const canvas = this.canvasAl();
    if (this.hDown) canvas?.removeEventListener('pointerdown', this.hDown);
    if (this.hMove) canvas?.removeEventListener('pointermove', this.hMove as any);
    if (this.hUp) window.removeEventListener('pointerup', this.hUp);
    if (this.hKey) window.removeEventListener('keydown', this.hKey);
    if (this.hKeyUp) window.removeEventListener('keyup', this.hKeyUp);
    this.hDown = this.hMove = this.hUp = null; this.hKey = this.hKeyUp = null;
    this.iptal();
    this.aktif = false;
  }

  aracSec(a: SketchArac): void { this.iptal(); this.arac = a; }

  iptal(): void {
    if (this.arac === 'spline' && this.noktalar.length >= 2) { this.sketch.ekle('spline', this.noktalar.slice()); this.degisti?.(); }   // Esc → spline tamamla
    this.noktalar = [];
    this.tegetYayModu = false; this.sonTeget = null;
    if (this.onizleme) { this.sahne.remove(this.onizleme); this.onizleme.geometry.dispose(); this.onizleme = null; }
    this.snapGorselTemizle();
    this.ciz();
  }

  // ---- pointer ----

  private duzlemGuncelle(): void {
    this.duzlemTHREE.setFromNormalAndCoplanarPoint(this.sketch.duzlem.normal, this.sketch.duzlem.origin);
  }

  /** Ham ray-plane noktası + snap çözümü (marker/inference dahil). */
  private snapHesapla(e: PointerEvent, canvas: HTMLCanvasElement): { nokta: ISketchNokta; sonuc: SnapSonuc } | null {
    const kamera = this.kameraAl();
    if (!kamera) return null;
    const rect = canvas.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, kamera as THREE.Camera);
    const hit = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.duzlemTHREE, hit)) return null;
    const ham = this.sketch.dunya2Duzlem(hit);
    this.snap.aktif = this.snapAcik && !this.shiftBasili; // Shift basılı → snap geçici kapalı
    const fareEkran = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const sonuc = this.snap.coz(this.sketch, ham, fareEkran, kamera as THREE.Camera, rect, this.gridAdim);
    return { nokta: sonuc.nokta, sonuc };
  }

  private duzlemNoktasi(e: PointerEvent, canvas: HTMLCanvasElement): ISketchNokta | null {
    return this.snapHesapla(e, canvas)?.nokta ?? null;
  }

  // ---- snap görselleri ----
  private snapGoster(sonuc: SnapSonuc, canvas: HTMLCanvasElement): void {
    const rect = canvas.getBoundingClientRect();
    // marker
    const sembol = FikoSnapMotoru.sembol(sonuc.tip);
    if (sembol) {
      if (!this.markerEl) {
        this.markerEl = document.createElement('div');
        Object.assign(this.markerEl.style, {
          position: 'fixed', zIndex: '9500', pointerEvents: 'none', transform: 'translate(-50%,-50%)',
          color: '#ffa726', font: '700 16px monospace', textShadow: '0 0 4px rgba(0,0,0,.8)',
        } as CSSStyleDeclaration);
        document.body.appendChild(this.markerEl);
      }
      this.markerEl.textContent = sembol;
      this.markerEl.style.left = `${rect.left + sonuc.ekran.x}px`;
      this.markerEl.style.top = `${rect.top + sonuc.ekran.y}px`;
      this.markerEl.style.display = 'block';
    } else if (this.markerEl) {
      this.markerEl.style.display = 'none';
    }
    // inference çizgileri
    if (sonuc.inference.length) {
      const pts: THREE.Vector3[] = [];
      for (const seg of sonuc.inference) { pts.push(this.sketch.duzlem2Dunya(seg.a), this.sketch.duzlem2Dunya(seg.b)); }
      if (!this.inferenceCizgi) {
        this.inferenceCizgi = new THREE.LineSegments(new THREE.BufferGeometry(), this.inferenceMat);
        this.inferenceCizgi.name = 'sketchInference'; this.inferenceCizgi.renderOrder = 5;
        this.sahne.add(this.inferenceCizgi);
      }
      this.inferenceCizgi.geometry.dispose();
      this.inferenceCizgi.geometry = new THREE.BufferGeometry().setFromPoints(pts);
      this.inferenceCizgi.computeLineDistances();
      this.inferenceCizgi.visible = true;
    } else if (this.inferenceCizgi) {
      this.inferenceCizgi.visible = false;
    }
  }

  private snapGorselTemizle(): void {
    if (this.markerEl) { this.markerEl.remove(); this.markerEl = null; }
    if (this.inferenceCizgi) { this.sahne.remove(this.inferenceCizgi); this.inferenceCizgi.geometry.dispose(); this.inferenceCizgi = null; }
  }

  konikTipi: 'parabol' | 'hiperbol' = 'parabol';   // konik aracı alt türü (komutla ayarlanır)
  private gerekenNokta(): number {
    if (this.arac === 'yay' || this.arac === 'slot' || this.arac === 'cember3n') return 3;
    if (this.arac === 'nokta') return 1;
    return 2;
  }

  private onDown(e: PointerEvent, canvas: HTMLCanvasElement): void {
    if (e.button !== 0) return;
    if (this.arac === 'sec') { this.surukleBaslat(e, canvas); return; }  // seç modu → sürükleme
    const p = this.duzlemNoktasi(e, canvas);
    if (!p) return;
    // Spline (çoklu kontrol noktası): her tık nokta ekler, Esc/sağ-tık tek spline oluşturur
    if (this.arac === 'spline') { this.noktalar.push(p); this.degisti?.(); this.ciz(); return; }
    // Polyline (çoklu çizgi): her tık bir segment ekler; G tuşu ile teğet yay (CATIA Profile). Esc/sağ-tık bitirir
    if (this.arac === 'polyline') {
      if (!this.noktalar.length) { this.noktalar.push(p); this.sonTeget = null; return; }
      const a = this.noktalar[this.noktalar.length - 1];
      if (this.tegetYayModu) {
        const yay = this.tegetYayUret(a, p);
        if (yay) { this.araciOtoCoincidence(this.sketch.ekle('yay', yay)); this.sonTeget = this.yayBitisTegeti(yay); }
        else { const b = this.yatayDikey(a, p); this.araciOtoCoincidence(this.sketch.ekle('cizgi', [a, b])); this.sonTeget = this.birim(a, b); }
      } else {
        const b = this.yatayDikey(a, p); this.araciOtoCoincidence(this.sketch.ekle('cizgi', [a, b])); this.sonTeget = this.birim(a, b);
      }
      this.noktalar = [p]; this.degisti?.(); this.ciz();
      return;
    }
    this.noktalar.push(p);
    if (this.noktalar.length >= this.gerekenNokta()) {
      this.elemanTamamla(this.noktalar);
      this.noktalar = [];
      this.onizlemeKaldir();
    }
  }

  private onMove(e: PointerEvent, canvas: HTMLCanvasElement): void {
    if (this.surukle) { this.surukleGuncelle(e, canvas); return; }
    if (this.arac === 'sec') return;
    const h = this.snapHesapla(e, canvas);
    if (!h) return;
    this.sonMouse = h.nokta;              // sayısal giriş canlı değerleri için
    this.sonMouseEkran = { x: e.clientX, y: e.clientY };   // panel imlece yakın
    this.snapGoster(h.sonuc, canvas);    // marker + inference
    if (this.noktalar.length) this.onizlemeGuncelle([...this.noktalar, h.nokta]);
    this.ciz();
    this.sayisalDegisti?.();             // sayısal giriş panelini güncelle (canlı değer)
  }

  /** Sayısal giriş paneli haber callback (mouse hareketinde canlı değer). */
  sayisalDegisti?: () => void;

  /** Aktif araç + yerleştirilmiş nokta sayısına göre sayısal giriş alanları (canlı değerlerle). */
  get sayisalAlanlar(): { anahtar: string; etiket: string; deger: number; birim: string }[] {
    const t = this.arac, n = this.noktalar.length, mp = this.sonMouse; if (!mp) return [];
    const r2 = (v: number) => Math.round(v * 100) / 100;
    if (t === 'nokta') return [{ anahtar: 'x', etiket: 'X', deger: r2(mp.x), birim: 'mm' }, { anahtar: 'y', etiket: 'Y', deger: r2(mp.y), birim: 'mm' }];
    if ((t === 'cizgi' || t === 'insacizgi' || t === 'polyline') && n >= 1) {
      const a = this.noktalar[n - 1], dx = mp.x - a.x, dy = mp.y - a.y;
      return [{ anahtar: 'uzunluk', etiket: 'Uzunluk', deger: r2(Math.hypot(dx, dy)), birim: 'mm' }, { anahtar: 'aci', etiket: 'Açı', deger: r2(Math.atan2(dy, dx) * 180 / Math.PI), birim: '°' }];
    }
    if (t === 'cember' && n >= 1) { const a = this.noktalar[0]; return [{ anahtar: 'r', etiket: 'Yarıçap', deger: r2(Math.hypot(mp.x - a.x, mp.y - a.y)), birim: 'mm' }]; }
    if ((t === 'dikdortgen' || t === 'dikdortgenMerkez') && n >= 1) {
      const a = this.noktalar[0], k = t === 'dikdortgenMerkez' ? 2 : 1;
      return [{ anahtar: 'en', etiket: 'En', deger: r2(Math.abs(mp.x - a.x) * k), birim: 'mm' }, { anahtar: 'boy', etiket: 'Boy', deger: r2(Math.abs(mp.y - a.y) * k), birim: 'mm' }];
    }
    if (t === 'elips' && n >= 1) { const a = this.noktalar[0]; return [{ anahtar: 'a', etiket: 'a', deger: r2(Math.abs(mp.x - a.x)), birim: 'mm' }, { anahtar: 'b', etiket: 'b', deger: r2(Math.abs(mp.y - a.y)), birim: 'mm' }]; }
    return [];
  }

  /** Sayısal değerleri uygula → elemanı tam ölçüyle tamamla (CATIA Sketch Tools girişi). */
  sayisalUygula(vals: Record<string, number>): void {
    const t = this.arac, n = this.noktalar.length, mp = this.sonMouse; if (!mp) return;
    const bitir = () => { this.noktalar = []; this.onizlemeKaldir(); this.degisti?.(); this.ciz(); this.sayisalDegisti?.(); };
    if (t === 'nokta') { this.sketch.ekle('nokta', [{ x: vals.x ?? mp.x, y: vals.y ?? mp.y }]); bitir(); return; }
    if ((t === 'cizgi' || t === 'insacizgi' || t === 'polyline') && n >= 1) {
      const a = this.noktalar[n - 1], dx = mp.x - a.x, dy = mp.y - a.y;
      const uz = (vals.uzunluk != null ? vals.uzunluk : Math.hypot(dx, dy)) || 1;
      const ac = vals.aci != null ? vals.aci * Math.PI / 180 : Math.atan2(dy, dx);
      const b = { x: a.x + Math.cos(ac) * uz, y: a.y + Math.sin(ac) * uz };
      if (t === 'polyline') { this.sketch.ekle('cizgi', [a, b]); this.noktalar = [b]; this.degisti?.(); this.ciz(); this.sayisalDegisti?.(); }
      else { const e = this.sketch.ekle('cizgi', [a, b]); if (t === 'insacizgi') { e.yapi = true; this.sketch.ciz(); } bitir(); }
      return;
    }
    if (t === 'cember' && n >= 1) { const a = this.noktalar[0]; this.sketch.ekle('cember', [a], (vals.r != null ? vals.r : Math.hypot(mp.x - a.x, mp.y - a.y)) || 1); bitir(); return; }
    if ((t === 'dikdortgen' || t === 'dikdortgenMerkez') && n >= 1) {
      const a = this.noktalar[0], sx = Math.sign(mp.x - a.x) || 1, sy = Math.sign(mp.y - a.y) || 1;
      const w = (vals.en != null ? vals.en : Math.abs(mp.x - a.x) * (t === 'dikdortgenMerkez' ? 2 : 1)) || 1;
      const h = (vals.boy != null ? vals.boy : Math.abs(mp.y - a.y) * (t === 'dikdortgenMerkez' ? 2 : 1)) || 1;
      if (t === 'dikdortgenMerkez') this.sketch.ekle('dikdortgen', [{ x: a.x - w / 2, y: a.y - h / 2 }, { x: a.x + w / 2, y: a.y + h / 2 }]);
      else this.sketch.ekle('dikdortgen', [a, { x: a.x + sx * w, y: a.y + sy * h }]);
      bitir(); return;
    }
    if (t === 'elips' && n >= 1) { const a = this.noktalar[0]; const el = this.sketch.ekle('elips', [a], (vals.a != null ? vals.a : Math.abs(mp.x - a.x)) || 1); el.r2 = (vals.b != null ? vals.b : Math.abs(mp.y - a.y)) || 1; this.sketch.ciz(); bitir(); return; }
  }

  private onUp(_e: PointerEvent): void {
    if (this.surukle) {
      const tasindi = this.surukle.tasindi;
      const s = this.surukle;
      this.surukle = null;
      this.sketch.sec(null);
      this.kontrolKilit?.(false);
      if (tasindi) {
        this.kisitCoz(s);          // sürükleme bitti → kalıcı kısıtları koru (kullanıcının tuttuğu nokta kilitli)
        this.degisti?.(); this.surukleBitti?.();
      }
      this.ciz();
    }
  }

  /** Sürükleme sonrası kalıcı kısıt çözümü: tutulan nokta(lar) kilitli kalır. */
  private kisitCoz(s: { el: any; key: string }): void {
    if (!this.sketch.kisitSolver.kisitlar.length) return;
    const kilit: string[] = [];
    const el = s.el;
    if (s.key === 'govde') {
      // gövde sürükleme: elemanın tüm noktaları kilitli (kullanıcı bütün elemanı taşıdı)
      const sayi = el.tip === 'cember' ? 1 : (el.tip === 'yay' ? 3 : 2);
      for (let i = 0; i < sayi; i++) { kilit.push(`${el.id}:px:${i}`, `${el.id}:py:${i}`); }
      if (el.tip === 'cember') kilit.push(`${el.id}:r:0`);
    } else if (s.key === 'merkez') { kilit.push(`${el.id}:px:0`, `${el.id}:py:0`);
    } else if (s.key === 'yaricap') { kilit.push(`${el.id}:r:0`);
    } else if (s.key.startsWith('n')) { const i = +s.key.slice(1); kilit.push(`${el.id}:px:${i}`, `${el.id}:py:${i}`); }
    this.sketch.kisitCoz(kilit);
  }

  // ---- interaktif sürükleme ----
  private surukleBaslat(e: PointerEvent, canvas: HTMLCanvasElement): void {
    const kamera = this.kameraAl(); if (!kamera) return;
    const rect = canvas.getBoundingClientRect();
    const fare = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const w = new THREE.Vector3();
    const ekran = (p2d: ISketchNokta) => { this.sketch.duzlem2Dunya(p2d, w).project(kamera as THREE.Camera); return { x: (w.x * 0.5 + 0.5) * rect.width, y: (-w.y * 0.5 + 0.5) * rect.height }; };
    // 1) en yakın KONTROL NOKTASI (<12px)
    let enKp: { el: any; key: string; d: number } | null = null;
    for (const el of this.sketch.elemanlar) {
      for (const kp of this.kontrolNoktalari(el)) {
        const s = ekran(kp.p); const d = Math.hypot(s.x - fare.x, s.y - fare.y);
        if (d < 12 && (!enKp || d < enKp.d)) enKp = { el, key: kp.key, d };
      }
    }
    const ham = this.duzlemHam(e, canvas);
    if (enKp && ham) {
      // PARAMETRİK: çakışık (bağlı) uçları yakala → birlikte taşınır, bütünden kopmaz
      let grup: { el: any; idx: number }[] | undefined;
      if (enKp.key === 'merkez' || /^n\d+$/.test(enKp.key)) {
        const idx = enKp.key === 'merkez' ? 0 : +enKp.key.slice(1);
        if (this.ucMu(enKp.el, idx)) grup = this.cakisanGrup(enKp.el.noktalar[idx]);
      }
      this.surukle = { el: enKp.el, key: enKp.key, son: ham, tasindi: false, grup };
      this.sketch.sec(enKp.el.id); this.kontrolKilit?.(true); return;
    }
    // 2) gövde sürükleme: imleç bir elemanın üstündeyse
    const el = this.elemanUstunde(fare, ekran);
    if (el && ham) { this.surukle = { el, key: 'govde', son: ham, tasindi: false }; this.sketch.sec(el.id); this.kontrolKilit?.(true); }
  }

  private surukleGuncelle(e: PointerEvent, canvas: HTMLCanvasElement): void {
    if (!this.surukle) return;
    const h = this.snapHesapla(e, canvas); if (!h) return;
    this.snapGoster(h.sonuc, canvas);
    this.surukle.tasindi = true;
    const sp = h.nokta; const el = this.surukle.el;
    if (this.surukle.key === 'govde') {
      const dx = sp.x - this.surukle.son.x, dy = sp.y - this.surukle.son.y;
      for (const p of el.noktalar) { p.x += dx; p.y += dy; }
      this.surukle.son = sp;
    } else if (this.surukle.key === 'yaricap') { const m = el.noktalar[0]; el.r = Math.max(0.5, Math.hypot(sp.x - m.x, sp.y - m.y));
    } else {
      // nokta/merkez sürükleme — çakışık grup varsa hepsi birlikte (bağlı çizgiler kopmaz, parametrik)
      if (this.surukle.grup && this.surukle.grup.length) { for (const g of this.surukle.grup) g.el.noktalar[g.idx] = { x: sp.x, y: sp.y }; }
      else { const i = this.surukle.key === 'merkez' ? 0 : +this.surukle.key.slice(1); el.noktalar[i] = { x: sp.x, y: sp.y }; }
    }
    this.sketch.ciz();
    this.ciz();
  }

  /** Verilen indeks bağlanabilir bir uç mu (çizgi/yay 0-1, nokta/çember 0; dikdörtgen hariç). */
  private ucMu(el: any, idx: number): boolean {
    if (el.tip === 'cizgi') return idx === 0 || idx === 1;
    if (el.tip === 'yay') return idx === 0 || idx === 1;   // 2 = orta nokta, uç değil
    if (el.tip === 'nokta' || el.tip === 'cember') return idx === 0;
    return false;
  }

  /** Bir konuma çakışık (tol içi) tüm bağlanabilir uçlar — birlikte sürüklenir. */
  private cakisanGrup(pos: ISketchNokta): { el: any; idx: number }[] {
    const tol = 0.6; const grup: { el: any; idx: number }[] = [];
    for (const el of this.sketch.elemanlar) {
      const idxler = el.tip === 'cizgi' || el.tip === 'yay' ? [0, 1] : (el.tip === 'nokta' || el.tip === 'cember' ? [0] : []);
      for (const i of idxler) { const p = el.noktalar[i]; if (p && Math.hypot(p.x - pos.x, p.y - pos.y) < tol) grup.push({ el, idx: i }); }
    }
    return grup;
  }

  /** Elemanın sürüklenebilir kontrol noktaları. */
  private kontrolNoktalari(el: any): { key: string; p: ISketchNokta }[] {
    if (el.tip === 'cizgi') return [{ key: 'n0', p: el.noktalar[0] }, { key: 'n1', p: el.noktalar[1] }];
    if (el.tip === 'dikdortgen') return [{ key: 'n0', p: el.noktalar[0] }, { key: 'n1', p: el.noktalar[1] }];
    if (el.tip === 'cember') { const m = el.noktalar[0]; return [{ key: 'merkez', p: m }, { key: 'yaricap', p: { x: m.x + (el.r || 0), y: m.y } }]; }
    if (el.tip === 'yay') return [{ key: 'n0', p: el.noktalar[0] }, { key: 'n1', p: el.noktalar[1] }, { key: 'n2', p: el.noktalar[2] }];
    return [{ key: 'n0', p: el.noktalar[0] }];
  }

  /** İmleç altındaki eleman (gövde sürükleme için, ekran-uzayı segment mesafesi). */
  private elemanUstunde(fare: { x: number; y: number }, ekran: (p: ISketchNokta) => { x: number; y: number }): any | null {
    let en: any = null, enD = 12;
    for (const el of this.sketch.elemanlar) {
      const pts = this.elemanNoktalari(el).map(ekran);
      let d = Infinity;
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i], b = pts[i + 1]; const dx = b.x - a.x, dy = b.y - a.y; const l2 = dx * dx + dy * dy;
        let t = l2 > 0 ? ((fare.x - a.x) * dx + (fare.y - a.y) * dy) / l2 : 0; t = Math.max(0, Math.min(1, t));
        d = Math.min(d, Math.hypot(fare.x - (a.x + dx * t), fare.y - (a.y + dy * t)));
      }
      if (d < enD) { enD = d; en = el; }
    }
    return en;
  }

  private elemanNoktalari(el: any): ISketchNokta[] {
    if (el.tip === 'cizgi') return [el.noktalar[0], el.noktalar[1]];
    if (el.tip === 'dikdortgen') { const [a, b] = el.noktalar; return [{ x: a.x, y: a.y }, { x: b.x, y: a.y }, { x: b.x, y: b.y }, { x: a.x, y: b.y }, { x: a.x, y: a.y }]; }
    if (el.tip === 'cember') { const m = el.noktalar[0], r = el.r || 0; const o = []; for (let i = 0; i <= 32; i++) { const t = i / 32 * Math.PI * 2; o.push({ x: m.x + Math.cos(t) * r, y: m.y + Math.sin(t) * r }); } return o; }
    if (el.tip === 'yay') return FikoSketch.yayNoktalari(el.noktalar[0], el.noktalar[1], el.noktalar[2]);
    return [el.noktalar[0]];
  }

  private duzlemHam(e: PointerEvent, canvas: HTMLCanvasElement): ISketchNokta | null {
    const kamera = this.kameraAl(); if (!kamera) return null;
    const rect = canvas.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, kamera as THREE.Camera);
    const hit = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.duzlemTHREE, hit)) return null;
    return this.sketch.dunya2Duzlem(hit);
  }

  private elemanTamamla(ns: ISketchNokta[]): void {
    if (this.arac === 'cizgi') { const [a, b] = ns; this.araciOtoCoincidence(this.sketch.ekle('cizgi', [a, this.yatayDikey(a, b)])); }
    else if (this.arac === 'insacizgi') { const [a, b] = ns; const e = this.sketch.ekle('cizgi', [a, this.yatayDikey(a, b)]); e.yapi = true; this.sketch.ciz(); }
    // CATIA: mouse ile yerleştirilen şablon/konik (merkez/tepe = ns[0], boyut = ns[1])
    else if (this.arac === 'sablonDik') { const c = ns[0], p = ns[1]; this.sketch.sablonYonluDikdortgen(c, Math.max(1, 2 * Math.abs(p.x - c.x)), Math.max(1, 2 * Math.abs(p.y - c.y)), 0); }
    else if (this.arac === 'sablonParalel') { const a = ns[0], p = ns[1]; this.sketch.sablonParalelkenar(a, Math.max(1, Math.abs(p.x - a.x)), Math.max(1, Math.hypot(p.x - a.x, p.y - a.y) * 0.7), 0, Math.PI / 3); }
    else if (this.arac === 'sablonKeyhole') { const c = ns[0], p = ns[1]; const d = Math.max(2, Math.hypot(p.x - c.x, p.y - c.y)); this.sketch.sablonAnahtarDeligi(c, d * 0.4, d, d * 0.18); }
    else if (this.arac === 'konik') { const a = ns[0], b = ns[1]; const el = this.sketch.ekle('konik', [a], Math.max(0.5, Math.abs(b.x - a.x) || 8)); el.r2 = Math.max(0.5, Math.abs(b.y - a.y) || 12); el.konikTip = this.konikTipi; this.sketch.ciz(); }
    else if (this.arac === 'dikdortgen') this.sketch.ekle('dikdortgen', [ns[0], ns[1]]);
    else if (this.arac === 'dikdortgenMerkez') { const c = ns[0], p = ns[1]; this.sketch.ekle('dikdortgen', [{ x: 2 * c.x - p.x, y: 2 * c.y - p.y }, p]); }   // merkezli dikdörtgen
    else if (this.arac === 'cember') { const [a, b] = ns; this.sketch.ekle('cember', [a], Math.hypot(b.x - a.x, b.y - a.y)); }
    else if (this.arac === 'cember3n') { const cc = FikoSketch.cevreCember(ns[0], ns[1], ns[2]); if (cc) this.sketch.ekle('cember', [cc.merkez], cc.r); }   // 3 noktadan çember
    else if (this.arac === 'yay') this.sketch.ekle('yay', [ns[0], ns[1], ns[2]]);
    else if (this.arac === 'nokta') this.sketch.ekle('nokta', [ns[0]]);
    else if (this.arac === 'cokgen') this.cokgenEkle(ns[0], ns[1]);
    else if (this.arac === 'slot') this.slotEkle(ns[0], ns[1], ns[2]);
    else if (this.arac === 'elips') { const a = ns[0], b = ns[1]; const el = this.sketch.ekle('elips', [a], Math.max(0.1, Math.abs(b.x - a.x))); el.r2 = Math.max(0.1, Math.abs(b.y - a.y)); this.sketch.ciz(); }
    this.degisti?.();
    this.ciz();
  }

  /** PARAMETRİK düzgün çokgen: TEK eleman (merkez + köşe + kenarSayısı) — patlatma YOK. */
  private cokgenEkle(merkez: ISketchNokta, kose: ISketchNokta): void {
    const n = Math.max(3, Math.min(20, this.cokgenKenar));
    const r = Math.hypot(kose.x - merkez.x, kose.y - merkez.y); if (r < 1e-4) return;
    const e = this.sketch.ekle('cokgen', [merkez, kose], r);   // noktalar[1]=ilk köşe (açı), r=yarıçap
    e.kenarSayisi = n;
    this.sketch.ciz();
  }

  /** PARAMETRİK oval slot: TEK eleman (iki merkez + yarıçap) — patlatma YOK. */
  private slotEkle(c1: ISketchNokta, c2: ISketchNokta, wp: ISketchNokta): void {
    const dx = c2.x - c1.x, dy = c2.y - c1.y, L = Math.hypot(dx, dy) || 1;
    const nx = -dy / L, ny = dx / L;
    const r = Math.abs((wp.x - c1.x) * nx + (wp.y - c1.y) * ny) || L * 0.3;
    this.sketch.ekle('slot', [c1, c2], r);
    this.sketch.ciz();
  }

  /** Auto-constraint: çizgi yataya/dikeye ~2.5° yakınsa tam yatay/dikey yapar. */
  private yatayDikey(a: ISketchNokta, b: ISketchNokta): ISketchNokta {
    if (!this.snapAcik) return b;
    const aci = Math.atan2(Math.abs(b.y - a.y), Math.abs(b.x - a.x)) * 180 / Math.PI;
    if (aci < 2.5) return { x: b.x, y: a.y };   // yatay
    if (aci > 87.5) return { x: a.x, y: b.y };  // dikey
    return b;
  }

  private birim(a: ISketchNokta, b: ISketchNokta): ISketchNokta { const dx = b.x - a.x, dy = b.y - a.y; const l = Math.hypot(dx, dy) || 1; return { x: dx / l, y: dy / l }; }

  /** PARAMETRİK: yeni çizilen elemanın uçları başka uçlara çakışıksa otomatik coincidence kısıtı (solver bağlantıyı bilir). */
  private araciOtoCoincidence(el: any): void {
    if (!this.snapAcik || !el) return;
    const tol = 0.6;
    const ucSayi = (e: any) => e.tip === 'cizgi' || e.tip === 'yay' ? 2 : (e.tip === 'nokta' || e.tip === 'cember' ? 1 : 0);
    const varMi = (aId: number, aUc: number, bId: number, bUc: number) => this.sketch.kisitSolver.kisitlar.some(k => k.tip === 'coincidence' && k.elemanlar.length === 2 &&
      k.elemanlar.some(x => x.elemanId === aId && (x.uc ?? 0) === aUc) && k.elemanlar.some(x => x.elemanId === bId && (x.uc ?? 0) === bUc));
    const n = ucSayi(el);
    for (let i = 0; i < n; i++) {
      const p = el.noktalar[i];
      for (const o of this.sketch.elemanlar) {
        if (o.id === el.id) continue;
        const on = ucSayi(o);
        for (let j = 0; j < on; j++) {
          const q = o.noktalar[j];
          if (q && Math.hypot(p.x - q.x, p.y - q.y) < tol && !varMi(el.id, i, o.id, j)) {
            this.sketch.kisitEkleKalici('coincidence', [{ elemanId: el.id, uc: i }, { elemanId: o.id, uc: j }]);
          }
        }
      }
    }
  }

  /** Teğet yay: önceki segment teğetine (sonTeget) teğet başlayıp p'ye varan yay → [bas, son, yayÜstü]. */
  private tegetYayUret(a: ISketchNokta, p: ISketchNokta): [ISketchNokta, ISketchNokta, ISketchNokta] | null {
    const t = this.sonTeget; if (!t) return null;
    const n = { x: -t.y, y: t.x };                  // teğete dik
    const dx = p.x - a.x, dy = p.y - a.y;
    const dn = dx * n.x + dy * n.y; if (Math.abs(dn) < 1e-6) return null;   // düz → yay yok
    const R = (dx * dx + dy * dy) / (2 * dn);
    const m = { x: a.x + n.x * R, y: a.y + n.y * R };   // merkez
    const orta = { x: (a.x + p.x) / 2, y: (a.y + p.y) / 2 };
    let mx = orta.x - m.x, my = orta.y - m.y; const ml = Math.hypot(mx, my) || 1; const ar = Math.abs(R);
    return [a, p, { x: m.x + mx / ml * ar, y: m.y + my / ml * ar }];   // mevcut 3-nokta yay formatı
  }
  /** Yayın bitiş (son) noktasındaki teğet yön. */
  private yayBitisTegeti(yay: [ISketchNokta, ISketchNokta, ISketchNokta]): ISketchNokta {
    const cc = FikoSketch.cevreCember(yay[0], yay[1], yay[2]);
    if (!cc) return this.birim(yay[0], yay[1]);
    const r = { x: yay[1].x - cc.merkez.x, y: yay[1].y - cc.merkez.y };   // merkez→son yarıçap
    const tnj = { x: -r.y, y: r.x }; const l = Math.hypot(tnj.x, tnj.y) || 1;   // teğet = yarıçapa dik
    let tt = { x: tnj.x / l, y: tnj.y / l };
    // yön: önceki teğetle aynı tarafta olsun
    if (this.sonTeget && (tt.x * this.sonTeget.x + tt.y * this.sonTeget.y) < 0) tt = { x: -tt.x, y: -tt.y };
    return tt;
  }

  private onizlemeGuncelle(ns: ISketchNokta[]): void {
    const pts: THREE.Vector3[] = [];
    const w = (n: ISketchNokta) => this.sketch.duzlem2Dunya(n);
    if (this.arac === 'polyline' && this.tegetYayModu && this.sonTeget && ns.length >= 2) {
      const yay = this.tegetYayUret(ns[0], ns[1]);
      if (yay) for (const q of FikoSketch.yayNoktalari(yay[0], yay[1], yay[2])) pts.push(w(q));
      else pts.push(w(ns[0]), w(this.yatayDikey(ns[0], ns[1])));
    }
    else if ((this.arac === 'cizgi' || this.arac === 'insacizgi' || this.arac === 'polyline') && ns.length >= 2) { pts.push(w(ns[0]), w(this.yatayDikey(ns[0], ns[1]))); }
    else if (this.arac === 'cokgen' && ns.length >= 2) {
      const m = ns[0], k = ns[1]; const n = Math.max(3, Math.min(20, this.cokgenKenar));
      const r = Math.hypot(k.x - m.x, k.y - m.y); const a0 = Math.atan2(k.y - m.y, k.x - m.x);
      for (let i = 0; i <= n; i++) { const t = a0 + i / n * Math.PI * 2; pts.push(w({ x: m.x + Math.cos(t) * r, y: m.y + Math.sin(t) * r })); }
    } else if (this.arac === 'slot' && ns.length >= 2) {
      const c1 = ns[0], c2 = ns[1]; const dx = c2.x - c1.x, dy = c2.y - c1.y, L = Math.hypot(dx, dy) || 1;
      const ux = dx / L, uy = dy / L, nx = -uy, ny = ux;
      const wp = ns[2] || { x: c2.x + nx * L * 0.3, y: c2.y + ny * L * 0.3 };
      const r = Math.abs((wp.x - c1.x) * nx + (wp.y - c1.y) * ny) || L * 0.3;
      const A1 = { x: c1.x + nx * r, y: c1.y + ny * r }, A2 = { x: c2.x + nx * r, y: c2.y + ny * r };
      const B1 = { x: c1.x - nx * r, y: c1.y - ny * r }, B2 = { x: c2.x - nx * r, y: c2.y - ny * r };
      pts.push(w(A1), w(A2));
      for (const yp of FikoSketch.yayNoktalari(A2, B2, { x: c2.x + ux * r, y: c2.y + uy * r })) pts.push(w(yp));
      pts.push(w(B1));
      for (const yp of FikoSketch.yayNoktalari(B1, A1, { x: c1.x - ux * r, y: c1.y - uy * r })) pts.push(w(yp));
    }
    else if (this.arac === 'dikdortgen' && ns.length >= 2) {
      const a = ns[0], b = ns[1];
      pts.push(w({ x: a.x, y: a.y }), w({ x: b.x, y: a.y }), w({ x: b.x, y: b.y }), w({ x: a.x, y: b.y }), w({ x: a.x, y: a.y }));
    } else if (this.arac === 'dikdortgenMerkez' && ns.length >= 2) {
      const c = ns[0], p = ns[1], a = { x: 2 * c.x - p.x, y: 2 * c.y - p.y }, b = p;
      pts.push(w({ x: a.x, y: a.y }), w({ x: b.x, y: a.y }), w({ x: b.x, y: b.y }), w({ x: a.x, y: b.y }), w({ x: a.x, y: a.y }));
    } else if (this.arac === 'cember3n' && ns.length >= 3) {
      const cc = FikoSketch.cevreCember(ns[0], ns[1], ns[2]); if (cc) for (let i = 0; i <= 48; i++) { const t = (i / 48) * Math.PI * 2; pts.push(w({ x: cc.merkez.x + Math.cos(t) * cc.r, y: cc.merkez.y + Math.sin(t) * cc.r })); }
    } else if (this.arac === 'cember' && ns.length >= 2) {
      const a = ns[0], b = ns[1]; const r = Math.hypot(b.x - a.x, b.y - a.y);
      for (let i = 0; i <= 48; i++) { const t = (i / 48) * Math.PI * 2; pts.push(w({ x: a.x + Math.cos(t) * r, y: a.y + Math.sin(t) * r })); }
    } else if (this.arac === 'elips' && ns.length >= 2) {
      const a = ns[0], b = ns[1], aa = Math.abs(b.x - a.x) || 0.1, bb = Math.abs(b.y - a.y) || 0.1;
      for (let i = 0; i <= 72; i++) { const t = (i / 72) * Math.PI * 2; pts.push(w({ x: a.x + Math.cos(t) * aa, y: a.y + Math.sin(t) * bb })); }
    } else if (this.arac === 'spline' && ns.length >= 2) {
      for (const sp of FikoSketch.splineNoktalari({ noktalar: ns } as any)) pts.push(w(sp));
    } else if (this.arac === 'yay') {
      if (ns.length === 2) { pts.push(w(ns[0]), w(ns[1])); }
      else if (ns.length >= 3) { for (const p of FikoSketch.yayNoktalari(ns[0], ns[1], ns[2])) pts.push(w(p)); }
    }
    // CATIA mouse-yerleştirme şablon/konik önizleme (ghost imleci takip eder)
    else if (this.arac === 'sablonDik' && ns.length >= 2) {
      const c = ns[0], p = ns[1], ex = Math.abs(p.x - c.x), ey = Math.abs(p.y - c.y);
      const k = [{ x: c.x - ex, y: c.y - ey }, { x: c.x + ex, y: c.y - ey }, { x: c.x + ex, y: c.y + ey }, { x: c.x - ex, y: c.y + ey }, { x: c.x - ex, y: c.y - ey }];
      for (const q of k) pts.push(w(q));
    } else if (this.arac === 'sablonParalel' && ns.length >= 2) {
      const a = ns[0], p = ns[1]; const en = Math.abs(p.x - a.x), yan = Math.hypot(p.x - a.x, p.y - a.y) * 0.7;
      const B = { x: a.x + en, y: a.y }, D = { x: a.x + Math.cos(Math.PI / 3) * yan, y: a.y + Math.sin(Math.PI / 3) * yan }; const C = { x: B.x + (D.x - a.x), y: B.y + (D.y - a.y) };
      for (const q of [a, B, C, D, a]) pts.push(w(q));
    } else if (this.arac === 'sablonKeyhole' && ns.length >= 2) {
      const c = ns[0], p = ns[1], d = Math.max(2, Math.hypot(p.x - c.x, p.y - c.y)), R = d * 0.4;
      for (let i = 0; i <= 36; i++) { const t = i / 36 * Math.PI * 2; pts.push(w({ x: c.x + Math.cos(t) * R, y: c.y + Math.sin(t) * R })); }
    } else if (this.arac === 'konik' && ns.length >= 2) {
      const a = ns[0], b = ns[1]; const el: any = { noktalar: [a], r: Math.max(0.5, Math.abs(b.x - a.x) || 8), r2: Math.max(0.5, Math.abs(b.y - a.y) || 12), konikTip: this.konikTipi };
      for (const q of FikoSketch.konikNoktalari(el)) pts.push(w(q));
    }
    if (!pts.length) return;
    if (!this.onizleme) { this.onizleme = new THREE.Line(new THREE.BufferGeometry(), this.onizlemeMat); this.onizleme.name = 'sketchOnizleme'; this.sahne.add(this.onizleme); }
    this.onizleme.geometry.dispose();
    this.onizleme.geometry = new THREE.BufferGeometry().setFromPoints(pts);
  }

  private onizlemeKaldir(): void {
    if (this.onizleme) { this.sahne.remove(this.onizleme); this.onizleme.geometry.dispose(); this.onizleme = null; }
  }

  dispose(): void { this.durdur(); this.snapGorselTemizle(); this.onizlemeMat.dispose(); this.inferenceMat.dispose(); }
}
